


























































































import { Component, Vue, Watch } from "vue-property-decorator";
import store from "@/store";
import moment from "moment";
import events from "App/events";
//  types
import { Header } from "@/types/VDataTable";
import { headers } from "@/components/FleetOverview/FleetOverviewTable/headers";
import { ExtendedVessel } from "@/types/vessel";
import { Incident } from "@/types/incident";
import { VesselGroup } from "@/types/vesselGroup";
import { MapMarker } from "@/types/mapMarker";
//  components
import FleetOverviewTable from "@/components/FleetOverview/FleetOverviewTable/index.vue";
import Map from "@/components/FleetOverview/Map/index.vue";
import VesselGroupFilter from "@/components/VesselGroup/VesselGroupFilter.vue";
//  modules
import { getModule } from "vuex-module-decorators";
import VesselsModule from "@/store/clients/Vessels.module";
import VesselGroupsModule from "@/store/clients/VesselGroups.module";
import IncidentsModule from "@/store/clients/Incidents.module";

const Vessels = getModule(VesselsModule, store);
const Incidents = getModule(IncidentsModule, store);
const VesselGroups = getModule(VesselGroupsModule, store);

@Component({
  components: {
    FleetOverviewTable,
    Map,
    VesselGroupFilter,
  },
})
export default class FleetOverview extends Vue {
  // @Data
  loading = false;
  searchQuery = "";
  selectedVesselGroup: VesselGroup | null = null;
  selectedGroupIds: number[] = [];
  vesselCompareNavigateHomeBinding: any = null;
  headers: Header[] = headers;
  isMapEnabled = true;
  selectedRows: Object[] = [];
  vesselsMissingPosition: MapMarker[] = [];
  mapKey = 0;

  // @Watchers
  @Watch("selectedGroupIds")
  @Watch("searchQuery")
  refreshDependentData(): void {
    // This will refresh the map with filtered vessels.
    this.mapKey += 1;
  }

  @Watch("isMapEnabled")
  updateStoredIsMapEnabled(isMapEnabled: boolean): void {
    localStorage.setItem("vesselOverview_isMapEnabled", isMapEnabled.toString());
  }

  @Watch("selectedGroupIds")
  updateStoredGroupIds(selectedGroupIds: number[]): void {
    localStorage.setItem("vesselOverview_selectedGroupIds", JSON.stringify(selectedGroupIds));
  }

  // @Getters
  get rows(): any {
    let rows = Vessels.extendedVessels.map((v, index) => {
      return {
        index: index,
        vesselName: {
          value: v.name,
          vesselId: v.id,
          link: `/Vessel/${v.id}/${encodeURIComponent(v.name)}/Diagnostics`,
          subtitle: v.imoNumber,
          missingPosition: this.vesselIdListMissingPosition.includes(v.id),
        },
        vesselGroup: {
          value: this.vesselGroupNameList(v.id),
          ids: this.vesselGroupIdList(v.id),
        },
        performance: {
          value: this.isDiagnosticsEnabled(v) ? v.performance?.performanceStatus : -1,
        },
        lastDataObserved: {
          value: moment.utc(v.lastExportDate).format("DD. MMM, YYYY HH:mm") ?? "N/A",
          subtitle: moment(v.lastExportDate).fromNow(),
        },
        alert: {
          value: this.isNotificationsEnabled(v) ? this.warningList(v.id).length + this.criticalList(v.id).length : -1,
          warningList: this.warningList(v.id),
          criticalList: this.criticalList(v.id),
        },
      };
    });
    // Filter by search query
    if (this.searchQuery && this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      rows = rows.filter(item => item.vesselName.value.toLowerCase().includes(query) ||
                        item.vesselName.subtitle.toString().toLocaleLowerCase().includes(query));
    }

    // Filter by selected vessel group
    if (this.selectedGroupIds.length > 0 && this.selectedGroupIds.some(id => this.vesselGroups.some(group => group.id === id))) {
      rows = rows.filter(row => {
        const groupIds = row.vesselGroup.ids;
        return this.selectedGroupIds.some(groupId => groupIds.includes(groupId));
      });
    }

    return rows;
  }

  get extendedVessels(): ExtendedVessel[] {
    return Vessels.extendedVessels;
  }

  get vesselGroups(): VesselGroup[] {
    return VesselGroups.vesselGroups;
  }

  get allOpenIncidents(): Incident[] {
    return Incidents.allOpenIncidents;
  }

  get themeClass(): string {
    return this.$vuetify.theme.dark ? "theme--dark" : "theme--light";
  }

  get selectedVessels(): ExtendedVessel[] {
    const vesselIds = this.selectedRows.map((r: any) => r.vesselName.vesselId);
    return Vessels.extendedVessels.filter(v => vesselIds.includes(v.id));
  }

  get filteredVessels(): ExtendedVessel[] {
    // Extract vessel IDs from filtered rows
    const filteredVesselIds = this.rows.map((row: { vesselName: { vesselId: number } }) => row.vesselName.vesselId);
    // Filter extended vessels based on the filtered vessel IDs
    return this.extendedVessels.filter(vessel => filteredVesselIds.includes(vessel.id));
  }

  get vesselIdListMissingPosition(): number[] {
    return Vessels.extendedVessels.filter(v => v.latitude === null || v.longitude === null).map(v => v.id);
  }

  get vesselsLoadingState(): boolean {
    return Vessels.vesselsLoadingState;
  }

  // @Methods
  vesselGroupNameList(vesselId: number): string {
    const groups = this.vesselGroups.filter(g => g.vesselIds.includes(vesselId));
    if (!groups.length) return "-";
    return groups.map(g => `${g.name}<br />`).join("");
  }

  vesselGroupIdList(vesselId: number): number[] {
    const groups = this.vesselGroups.filter(g => g.vesselIds.includes(vesselId));
    if (!groups.length) return [];
    return groups.map(g => g.id);
  }

  isNotificationsEnabled(vessel: ExtendedVessel): boolean {
    if (!vessel) return false;
    return vessel.features.some(feature => feature.name === "Notifications");
  }

  isDiagnosticsEnabled(vessel: ExtendedVessel): boolean {
    if (!vessel) return false;
    return vessel.features.some(feature => feature.name === "Diagnostics");
  }

  warningList(vesselId: number): Incident[] {
    // If a condition has both an open warning and critical incident, we only want to show the critical one.
    const openWarningIncidents = this.allOpenIncidents.filter(i => i.vesselId === vesselId && i.severity === "Warning");
    const criticalConditionIds = new Set(this.allOpenIncidents.filter(i => i.vesselId === vesselId && i.severity === "Critical").map(i => i.conditionId));
    return openWarningIncidents.filter(i => !criticalConditionIds.has(i.conditionId));
  }

  criticalList(vesselId: number): Incident[] {
    return this.allOpenIncidents.filter(i => i.vesselId === vesselId && i.severity === "Critical");
  }

  initNavigationHomeBinding(): void {
    this.vesselCompareNavigateHomeBinding = events.navigatedHome.add(() => {
      this.selectedRows = [];
    });
  }

  initializeMapSettings(): void {
    this.isMapEnabled = localStorage.getItem("vesselOverview_isMapEnabled") === "false" ? false : true;
    const storedGroupIds = localStorage.getItem("vesselOverview_selectedGroupIds");
    if (storedGroupIds) this.selectedGroupIds = JSON.parse(storedGroupIds);
  }

  setInitialGroups() {
    if (this.selectedGroupIds.length === 0 && this.vesselGroups.length > 0) {
      this.selectedGroupIds = this.vesselGroups.map(group => group.id);
    }
  }

  async initDataLoad(): Promise<void> {
    this.initializeMapSettings();
    this.loading = true;
    await Vessels.refreshExtendedVessels();
    this.loading = false;
    await VesselGroups.getAll();
    this.setInitialGroups();
  }

  // @Hooks
  async created(): Promise<void> {
    // Initialize navigation home binding
    this.initNavigationHomeBinding();
    await this.initDataLoad();
  }
}
